<template>
    <AppDialog
        v-model="isOpen"
        size="lg"
        :persistent="isRevealStep"
        :hide-close="isRevealStep"
        @close="onDialogClosed"
    >
        <template #header>
            <h2 class="text-lg font-semibold text-theme-base">
                {{ isRevealStep ? "Copia tu api key" : "Nueva api key" }}
            </h2>
        </template>

        <div v-if="isRevealStep" class="space-y-4">
            <div
                class="flex items-start gap-2 rounded-lg bg-theme-hover border border-theme-border p-3 text-sm text-theme-base"
            >
                <span>⚠️</span>
                <span>
                    Este es el único momento en que verás el secreto completo.
                    Cópialo y guárdalo en un lugar seguro antes de cerrar esta
                    ventana.
                </span>
            </div>

            <div>
                <label class="text-sm text-theme-muted block mb-1">
                    Tenant ID
                </label>
                <div
                    class="flex items-center gap-2 rounded-lg border border-theme-border bg-theme-input px-3 py-2"
                >
                    <span class="font-mono text-sm text-theme-base break-all">
                        {{ tenantId }}
                    </span>
                </div>
            </div>

            <div>
                <label class="text-sm text-theme-muted block mb-1">
                    Api key
                </label>
                <div
                    class="flex items-center gap-2 rounded-lg border border-theme-border bg-theme-input px-3 py-2"
                >
                    <span class="font-mono text-sm text-theme-base break-all">
                        {{ createdKey.apiKey }}
                    </span>
                </div>
                <div class="mt-2 flex justify-end gap-2">
                    <app-button size="small" border @click="downloadApiKey">
                        Descargar
                    </app-button>
                    <app-button size="small" border @click="copyApiKey">
                        {{ copied ? "Copiado" : "Copiar" }}
                    </app-button>
                </div>
            </div>

            <div>
                <p class="text-sm text-theme-muted mb-1">Permisos</p>
                <api-key-permissions-summary
                    :permissions="createdKey.permissions || []"
                    :groups="permissionGroups"
                />
            </div>

            <p class="text-xs text-theme-muted">
                Luego la vas a identificar en la lista por su prefijo:
                <span class="font-mono">{{ createdKey.keyPrefix }}…</span>
            </p>

            <div class="rounded-lg border border-theme-border p-3 text-sm">
                <p class="font-medium text-theme-base mb-2">Cómo usarla</p>
                <p class="text-theme-muted mb-2">
                    Envía estos dos headers en cada request:
                </p>
                <pre
                    class="rounded-lg bg-theme-input p-2 text-xs font-mono text-theme-base whitespace-pre-wrap break-all"
                >
X-Tenant-Id: {{ tenantId }}
X-Api-Key: {{ createdKey.apiKey }}</pre
                >
                <p class="text-theme-muted mt-3 mb-2">Ejemplo:</p>
                <pre
                    class="rounded-lg bg-theme-input p-2 text-xs font-mono text-theme-base whitespace-pre-wrap break-all"
                >
curl "{{ marketplaceApiUrl }}/api/products" \
  -H "X-Tenant-Id: {{ tenantId }}" \
  -H "X-Api-Key: {{ createdKey.apiKey }}"</pre
                >
                <WikiLearnMoreLink topic="integraciones-api" class="mt-3" />
            </div>
        </div>

        <div v-else class="space-y-4">
            <custom-input
                v-model="form.name"
                label="Nombre *"
                placeholder="Ej: Integración con el ERP"
                maxlength="120"
                :error="errors.name || ''"
            />

            <div>
                <label class="text-sm text-theme-muted block mb-2">
                    Vencimiento
                </label>
                <div class="flex flex-wrap gap-2">
                    <button
                        v-for="option in expirationOptions"
                        :key="option.label"
                        type="button"
                        class="rounded-lg border px-3 py-2 text-sm font-medium transition"
                        :class="
                            form.expiresInDays === option.value
                                ? 'border-theme-primary bg-theme-hover text-theme-primary'
                                : 'border-theme-border text-theme-muted'
                        "
                        @click="form.expiresInDays = option.value"
                    >
                        {{ option.label }}
                    </button>
                </div>
            </div>

            <div>
                <div
                    class="flex flex-wrap items-baseline justify-between gap-2"
                >
                    <label class="text-sm text-theme-muted block">
                        Permisos *
                    </label>
                    <span class="text-xs text-theme-muted">
                        {{ form.permissions.length }}
                        {{
                            form.permissions.length === 1
                                ? "permiso elegido"
                                : "permisos elegidos"
                        }}
                    </span>
                </div>
                <p class="text-xs text-theme-muted mt-1 mb-3">
                    Elige solo lo que la integración necesita. Los permisos en
                    gris no están en tu rol, así que no los puedes dar. Después
                    de crear la key no se pueden cambiar.
                </p>

                <p v-if="isLoadingPermissions" class="text-sm text-theme-muted">
                    Cargando permisos…
                </p>
                <p
                    v-else-if="!permissionGroups.length"
                    class="text-sm text-theme-muted"
                >
                    No hay permisos disponibles para asignar.
                </p>
                <div v-else class="space-y-4">
                    <div
                        v-for="group in permissionGroups"
                        :key="group.resource"
                    >
                        <div
                            class="flex items-center justify-between gap-2 border-b border-theme-border pb-2 mb-2"
                        >
                            <custom-checkbox
                                :checked="isGroupSelected(group)"
                                :disabled="!grantableSlugs(group).length"
                                @change="toggleGroup(group)"
                            >
                                <span class="font-medium">
                                    {{ resourceLabel(group.resource) }}
                                </span>
                            </custom-checkbox>
                            <span class="text-xs text-theme-muted">
                                {{ selectedCount(group) }} de
                                {{ group.permissions.length }}
                            </span>
                        </div>
                        <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            <custom-checkbox
                                v-for="permission in group.permissions"
                                :key="permission.slug"
                                v-model="form.permissions"
                                :val="permission.slug"
                                :disabled="!permission.grantable"
                            >
                                <span class="block">{{ permission.name }}</span>
                                <span
                                    class="block font-mono text-xs text-theme-muted"
                                >
                                    {{ permission.slug }}
                                </span>
                            </custom-checkbox>
                        </div>
                    </div>
                </div>
                <p
                    v-if="errors.permissions"
                    class="text-xs text-theme-danger mt-2"
                >
                    {{ errors.permissions }}
                </p>
            </div>
        </div>

        <template #footer>
            <div v-if="isRevealStep" class="flex justify-end">
                <app-button color="success" @click="isOpen = false">
                    Ya la copié, cerrar
                </app-button>
            </div>
            <div v-else class="flex flex-col sm:flex-row justify-end gap-2">
                <button
                    class="rounded-lg border border-theme-border px-4 py-2 text-sm font-medium text-theme-muted transition hover:bg-theme-hover"
                    @click="isOpen = false"
                >
                    Cancelar
                </button>
                <app-button
                    color="success"
                    :loading="isSaving"
                    :disabled="!canSubmit"
                    @click="submit"
                >
                    Crear api key
                </app-button>
            </div>
        </template>
    </AppDialog>
</template>

<script>
import planLimitError from "@/mixins/planLimitError";
import { mapActions, mapGetters, mapState } from "vuex";

import { MARKETPLACE_API_URL } from "@/common/constants";
import { apiKeyResourceLabel } from "@/common/fields/api-key-permissions";

import AppDialog from "@/components/utils/AppDialog.vue";
import CustomCheckbox from "@/components/utils/CustomCheckbox.vue";
import CustomInput from "@/components/utils/CustomInput.vue";
import WikiLearnMoreLink from "@/components/utils/WikiLearnMoreLink.vue";

import ApiKeyPermissionsSummary from "./ApiKeyPermissionsSummary.vue";

const EXPIRATION_OPTIONS = [
    { value: 30, label: "30 días" },
    { value: 90, label: "90 días" },
    { value: 180, label: "180 días" },
    { value: 365, label: "365 días" },
    { value: null, label: "Sin vencimiento" }
];

const DEFAULT_FORM = () => ({
    name: "",
    expiresInDays: 30,
    permissions: []
});

export default {
    name: "ApiKeyFormDialog",
    components: {
        ApiKeyPermissionsSummary,
        AppDialog,
        CustomCheckbox,
        CustomInput,
        WikiLearnMoreLink
    },
    mixins: [planLimitError],
    model: {
        prop: "value",
        event: "input"
    },
    props: {
        value: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            step: "form",
            form: DEFAULT_FORM(),
            errors: {},
            isSaving: false,
            createdKey: null,
            copied: false,
            permissionGroups: [],
            isLoadingPermissions: false
        };
    },
    computed: {
        ...mapState("auth", ["tenantId"]),
        ...mapGetters("control", ["backgroundColor"]),
        isOpen: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit("input", value);
            }
        },
        isRevealStep() {
            return this.step === "reveal";
        },
        expirationOptions() {
            return EXPIRATION_OPTIONS;
        },
        marketplaceApiUrl() {
            return MARKETPLACE_API_URL;
        },
        canSubmit() {
            return (
                !this.isLoadingPermissions && this.form.permissions.length > 0
            );
        }
    },
    watch: {
        value(isOpen) {
            if (isOpen) this.loadPermissionGroups();
        }
    },
    methods: {
        ...mapActions("apiKeys", [
            "createApiKey",
            "ensureApiKeyPermissionCatalog"
        ]),
        async loadPermissionGroups() {
            this.isLoadingPermissions = true;
            try {
                this.permissionGroups =
                    await this.ensureApiKeyPermissionCatalog();
                this.isLoadingPermissions = false;
            } catch (error) {
                this.isLoadingPermissions = false;
                this.$sound.play("error");
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        resourceLabel(resource) {
            return apiKeyResourceLabel(resource);
        },
        grantableSlugs(group) {
            return group.permissions
                .filter((permission) => permission.grantable)
                .map((permission) => permission.slug);
        },
        selectedCount(group) {
            return group.permissions.filter((permission) =>
                this.form.permissions.includes(permission.slug)
            ).length;
        },
        isGroupSelected(group) {
            const slugs = this.grantableSlugs(group);
            return (
                slugs.length > 0 &&
                slugs.every((slug) => this.form.permissions.includes(slug))
            );
        },
        toggleGroup(group) {
            const groupSlugs = group.permissions.map(
                (permission) => permission.slug
            );
            if (this.isGroupSelected(group)) {
                this.form.permissions = this.form.permissions.filter(
                    (slug) => !groupSlugs.includes(slug)
                );
                return;
            }
            const toAdd = this.grantableSlugs(group).filter(
                (slug) => !this.form.permissions.includes(slug)
            );
            this.form.permissions = [...this.form.permissions, ...toAdd];
        },
        validateForm() {
            const errors = {};
            if (!this.form.name.trim()) {
                errors.name = "El nombre es obligatorio";
            }
            if (!this.form.permissions.length) {
                errors.permissions = "Elige al menos un permiso";
            }
            this.errors = errors;
            return Object.keys(errors).length === 0;
        },
        async submit() {
            if (!this.validateForm() || this.isSaving) return;

            this.isSaving = true;
            try {
                const data = await this.createApiKey({
                    name: this.form.name.trim(),
                    expiresInDays: this.form.expiresInDays,
                    permissions: [...this.form.permissions]
                });
                this.isSaving = false;
                this.createdKey = data;
                this.step = "reveal";
                this.$sound.play("success");
                this.$emit("created");
            } catch (error) {
                this.isSaving = false;
                if (this.openPlanUpgradeForLimit(error)) {
                    this.isOpen = false;
                    return;
                }
                this.$sound.play("error");
                await this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        async copyApiKey() {
            if (!this.createdKey) return;
            await navigator.clipboard.writeText(this.createdKey.apiKey);
            this.copied = true;
            setTimeout(() => {
                this.copied = false;
            }, 1500);
        },
        downloadApiKey() {
            if (!this.createdKey) return;
            const content = [
                "tenantId,apiKey",
                `${this.tenantId},${this.createdKey.apiKey}`
            ].join("\n");
            const file = new Blob([content], { type: "text/csv" });
            const link = document.createElement("a");
            link.href = URL.createObjectURL(file);
            link.download = `api-key-${this.createdKey.keyPrefix}.csv`;
            link.click();
        },
        onDialogClosed() {
            this.step = "form";
            this.form = DEFAULT_FORM();
            this.errors = {};
            this.isSaving = false;
            this.createdKey = null;
            this.copied = false;
        }
    }
};
</script>
